import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar/Navbar';
import Footer from '../components/Footer/Footer';
import { useAuth } from '../context/AuthContext';
import { getMyOrderRequests, getMyServiceBookings, getMyMessages, updateProfile } from '../utils/api';

const MyAccountPage = () => {
  const { user, loginUser, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [orderRequests, setOrderRequests] = useState([]);
  const [serviceBookings, setServiceBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    password: '',
    confirmPassword: ''
  });
  const [profileMsg, setProfileMsg] = useState('');
  const [profileError, setProfileError] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) return navigate('/login');
    fetchData('orders');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (tab) => {
    setLoading(true);
    try {
      if (tab === 'orders') {
        const { data } = await getMyOrderRequests({ phone: user.phone, email: user.email });
        setOrderRequests(data);
      } else if (tab === 'services') {
        const { data } = await getMyServiceBookings({ phone: user.phone, email: user.email });
        setServiceBookings(data);
      } else if (tab === 'messages') {
        const { data } = await getMyMessages();
        setMessages(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'profile') fetchData(tab);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMsg('');
    if (profileForm.password && profileForm.password !== profileForm.confirmPassword) {
      return setProfileError('Passwords do not match');
    }
    try {
      const { data } = await updateProfile({
        name: profileForm.name,
        phone: profileForm.phone,
        address: profileForm.address,
        ...(profileForm.password && { password: profileForm.password })
      });
      loginUser(data);
      setProfileMsg('Profile updated successfully!');
      setProfileForm({ ...profileForm, password: '', confirmPassword: '' });
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Update failed');
    }
  };

  const statusColor = (status) => {
    const colors = {
      pending: '#f59e0b',
      contacted: '#3b82f6',
      confirmed: '#8b5cf6',
      completed: '#22c55e',
      cancelled: '#ef4444',
      unread: '#f59e0b',
      read: '#3b82f6',
      replied: '#22c55e'
    };
    return colors[status] || '#aaa';
  };

  const statusSteps = {
    order: ['pending', 'contacted', 'confirmed'],
    service: ['pending', 'confirmed', 'completed'],
  };

  const StatusTimeline = ({ status, type }) => {
    const steps = statusSteps[type];
    const currentIndex = steps.indexOf(status);
    return (
      <div style={styles.timeline}>
        {steps.map((step, i) => (
          <div key={i} style={styles.timelineStep}>
            <div style={{
              ...styles.timelineDot,
              background: i <= currentIndex ? '#e63946' : '#e8eaf0',
              border: `2px solid ${i <= currentIndex ? '#e63946' : '#e8eaf0'}`
            }}>
              {i <= currentIndex && <span style={styles.timelineTick}>✓</span>}
            </div>
            {i < steps.length - 1 && (
              <div style={{
                ...styles.timelineLine,
                background: i < currentIndex ? '#e63946' : '#e8eaf0'
              }} />
            )}
            <p style={{
              ...styles.timelineLabel,
              color: i <= currentIndex ? '#e63946' : '#aaa',
              fontWeight: i === currentIndex ? 'bold' : 'normal'
            }}>
              {step.charAt(0).toUpperCase() + step.slice(1)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ background: '#f0f2f5', minHeight: '100vh' }}>
      <Navbar />
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.avatar}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={styles.welcomeTitle}>My Account</h1>
            <p style={styles.welcomeSubtitle}>Welcome back, {user?.name} 👋</p>
          </div>
        </div>

        <div style={styles.layout}>
          {/* Sidebar */}
          <div style={styles.sidebar}>
            {[
              { id: 'orders', icon: '📦', label: 'My Orders', count: orderRequests.length },
              { id: 'services', icon: '🔧', label: 'My Services', count: serviceBookings.length },
              { id: 'messages', icon: '💬', label: 'My Messages', count: messages.length },
              { id: 'profile', icon: '👤', label: 'My Profile', count: null },
            ].map(tab => (
              <button
                key={tab.id}
                style={{
                  ...styles.sidebarBtn,
                  background: activeTab === tab.id ? '#fff5f5' : 'transparent',
                  color: activeTab === tab.id ? '#e63946' : '#555',
                  borderLeft: activeTab === tab.id ? '3px solid #e63946' : '3px solid transparent'
                }}
                onClick={() => handleTabChange(tab.id)}
              >
                <span style={styles.sidebarIcon}>{tab.icon}</span>
                <span style={styles.sidebarLabel}>{tab.label}</span>
                {tab.count !== null && tab.count > 0 && (
                  <span style={styles.sidebarCount}>{tab.count}</span>
                )}
              </button>
            ))}
            <button
              style={styles.logoutBtn}
              onClick={() => { logoutUser(); navigate('/'); }}
            >
              🚪 Logout
            </button>
          </div>

          {/* Main Content */}
          <div style={styles.main}>
            {loading && (
              <div style={styles.loadingBox}>
                <p style={styles.loadingText}>Loading...</p>
              </div>
            )}

            {/* ── MY ORDERS ── */}
            {activeTab === 'orders' && !loading && (
              <div>
                <h2 style={styles.sectionTitle}>My Order Requests</h2>
                {orderRequests.length === 0 ? (
                  <div style={styles.emptyBox}>
                    <span style={styles.emptyIcon}>📦</span>
                    <p style={styles.emptyTitle}>No orders yet</p>
                    <p style={styles.emptyText}>Browse our products and place your first order</p>
                    <button style={styles.actionBtn} onClick={() => navigate('/products')}>
                      Browse Products →
                    </button>
                  </div>
                ) : (
                  orderRequests.map(req => (
                    <div key={req._id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div>
                          <p style={styles.cardId}>Order #{req._id.slice(-8).toUpperCase()}</p>
                          <p style={styles.cardDate}>{new Date(req.createdAt).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                        <span style={{ ...styles.statusBadge, background: `${statusColor(req.status)}15`, color: statusColor(req.status), border: `1px solid ${statusColor(req.status)}40` }}>
                          {req.status.toUpperCase()}
                        </span>
                      </div>

                      <StatusTimeline status={req.status} type="order" />

                      <div style={styles.cardItems}>
                        {req.items.map((item, i) => (
                          <div key={i} style={styles.cardItem}>
                            <div style={styles.itemDot} />
                            <span style={styles.itemText}>{item.brand} {item.name} — {item.size}</span>
                            <span style={styles.itemPrice}>x{item.quantity} — $  {(item.price * item.quantity).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      <div style={styles.cardFooter}>
                        <div style={styles.cardAddress}>
                          📍 {req.address}, {req.city}
                        </div>
                        <div style={styles.cardTotal}>
                          Total: <strong style={{ color: '#e63946' }}>$ {req.subtotal?.toLocaleString()}</strong>
                        </div>
                      </div>

                      {req.status === 'pending' && (
                        <div style={styles.pendingNote}>
                          ⏳ Your order is under review. We will contact you on <strong>{req.phone}</strong> shortly.
                        </div>
                      )}
                      {req.status === 'contacted' && (
                        <div style={styles.contactedNote}>
                          📞 We have contacted you. Please confirm your order.
                        </div>
                      )}
                      {req.status === 'confirmed' && (
                        <div style={styles.confirmedNote}>
                          ✅ Your order is confirmed! Thank you for shopping with us.
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── MY SERVICES ── */}
            {activeTab === 'services' && !loading && (
              <div>
                <h2 style={styles.sectionTitle}>My Service Bookings</h2>
                {serviceBookings.length === 0 ? (
                  <div style={styles.emptyBox}>
                    <span style={styles.emptyIcon}>🔧</span>
                    <p style={styles.emptyTitle}>No service bookings yet</p>
                    <p style={styles.emptyText}>Book a service and track it here</p>
                    <button style={styles.actionBtn} onClick={() => navigate('/service-booking')}>
                      Book a Service →
                    </button>
                  </div>
                ) : (
                  serviceBookings.map(booking => (
                    <div key={booking._id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div>
                          <p style={styles.cardId}>Booking #{booking._id.slice(-8).toUpperCase()}</p>
                          <p style={styles.cardDate}>{new Date(booking.createdAt).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                        <span style={{ ...styles.statusBadge, background: `${statusColor(booking.status)}15`, color: statusColor(booking.status), border: `1px solid ${statusColor(booking.status)}40` }}>
                          {booking.status.toUpperCase()}
                        </span>
                      </div>

                      <StatusTimeline status={booking.status} type="service" />

                      <div style={styles.serviceInfo}>
                        <div style={styles.serviceInfoItem}>
                          <span style={styles.serviceInfoLabel}>🔧 Main Service</span>
                          <span style={styles.serviceInfoValue}>{booking.service}</span>
                        </div>
                        {booking.additionalServices?.length > 0 && (
                          <div style={styles.serviceInfoItem}>
                            <span style={styles.serviceInfoLabel}>➕ Additional</span>
                            <span style={styles.serviceInfoValue}>{booking.additionalServices.join(', ')}</span>
                          </div>
                        )}
                        <div style={styles.serviceInfoItem}>
                          <span style={styles.serviceInfoLabel}>🚗 Vehicle</span>
                          <span style={styles.serviceInfoValue}>{booking.vehicleBrand} {booking.vehicleModel} {booking.vehicleYear}</span>
                        </div>
                        {booking.preferredDate && (
                          <div style={styles.serviceInfoItem}>
                            <span style={styles.serviceInfoLabel}>📅 Date</span>
                            <span style={styles.serviceInfoValue}>{booking.preferredDate} {booking.preferredTime && `at ${booking.preferredTime}`}</span>
                          </div>
                        )}
                      </div>

                      {booking.status === 'pending' && (
                        <div style={styles.pendingNote}>
                          ⏳ Booking received. We will confirm your appointment on <strong>{booking.phone}</strong> soon.
                        </div>
                      )}
                      {booking.status === 'confirmed' && (
                        <div style={styles.contactedNote}>
                          📞 Your appointment is confirmed! Please arrive on time.
                        </div>
                      )}
                      {booking.status === 'completed' && (
                        <div style={styles.confirmedNote}>
                          ✅ Service completed. Thank you for choosing TireStore!
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── MY MESSAGES ── */}
            {activeTab === 'messages' && !loading && (
              <div>
                <h2 style={styles.sectionTitle}>My Messages</h2>
                {messages.length === 0 ? (
                  <div style={styles.emptyBox}>
                    <span style={styles.emptyIcon}>💬</span>
                    <p style={styles.emptyTitle}>No messages yet</p>
                    <p style={styles.emptyText}>Send us a message and track responses here</p>
                    <button style={styles.actionBtn} onClick={() => { navigate('/'); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300); }}>
                      Contact Us →
                    </button>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div key={msg._id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div>
                          <p style={styles.cardId}>Message #{msg._id.slice(-8).toUpperCase()}</p>
                          <p style={styles.cardDate}>{new Date(msg.createdAt).toLocaleDateString('en-PK', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                        <span style={{ ...styles.statusBadge, background: `${statusColor(msg.status)}15`, color: statusColor(msg.status), border: `1px solid ${statusColor(msg.status)}40` }}>
                          {msg.status.toUpperCase()}
                        </span>
                      </div>

                      <div style={styles.messageBox}>
                        <p style={styles.messageLabel}>Your Message:</p>
                        <p style={styles.messageText}>{msg.message}</p>
                      </div>

                      {msg.adminReply ? (
                        <div style={styles.replyBox}>
                          <p style={styles.replyLabel}>✅ Our Reply:</p>
                          <p style={styles.replyText}>{msg.adminReply}</p>
                        </div>
                      ) : (
                        <div style={styles.pendingNote}>
                          ⏳ We have received your message and will reply soon.
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── MY PROFILE ── */}
            {activeTab === 'profile' && (
              <div>
                <h2 style={styles.sectionTitle}>My Profile</h2>
                <div style={styles.profileCard}>
                  {profileMsg && <div style={styles.successBox}>✅ {profileMsg}</div>}
                  {profileError && <div style={styles.errorBox}>⚠️ {profileError}</div>}

                  <form onSubmit={handleProfileSubmit}>
                    <div style={styles.profileGrid}>
                      <div style={styles.field}>
                        <label style={styles.label}>Full Name</label>
                        <input
                          style={styles.input}
                          type="text"
                          value={profileForm.name}
                          onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                          placeholder="Your full name"
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>Email Address</label>
                        <input
                          style={{ ...styles.input, background: '#f0f2f5', cursor: 'not-allowed' }}
                          type="email"
                          value={user?.email}
                          disabled
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>Phone Number</label>
                        <input
                          style={styles.input}
                          type="text"
                          value={profileForm.phone}
                          onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                          placeholder="+92 300 1234567"
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>Address</label>
                        <input
                          style={styles.input}
                          type="text"
                          value={profileForm.address}
                          onChange={e => setProfileForm({ ...profileForm, address: e.target.value })}
                          placeholder="Your address"
                        />
                      </div>
                    </div>

                    <div style={styles.divider} />
                    <p style={styles.passwordTitle}>Change Password <span style={styles.optional}>(optional)</span></p>

                    <div style={styles.profileGrid}>
                      <div style={styles.field}>
                        <label style={styles.label}>New Password</label>
                        <input
                          style={styles.input}
                          type="password"
                          value={profileForm.password}
                          onChange={e => setProfileForm({ ...profileForm, password: e.target.value })}
                          placeholder="Leave blank to keep current"
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>Confirm Password</label>
                        <input
                          style={styles.input}
                          type="password"
                          value={profileForm.confirmPassword}
                          onChange={e => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                          placeholder="Repeat new password"
                        />
                      </div>
                    </div>

                    <button type="submit" style={styles.saveBtn}>
                      💾 Save Changes
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container: { maxWidth: '1100px', margin: '0 auto', padding: '40px 20px' },
  header: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px', background: '#fff', padding: '24px 28px', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  avatar: { width: '60px', height: '60px', background: '#e63946', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '24px', fontWeight: 'bold', flexShrink: 0 },
  welcomeTitle: { color: '#1a1a2e', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  welcomeSubtitle: { color: '#888', fontSize: '14px', margin: '4px 0 0' },
  layout: { display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px' },
  sidebar: { background: '#fff', borderRadius: '16px', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0', height: 'fit-content', display: 'flex', flexDirection: 'column', gap: '4px' },
  sidebarBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s', textAlign: 'left', width: '100%' },
  sidebarIcon: { fontSize: '18px', flexShrink: 0 },
  sidebarLabel: { flex: 1 },
  sidebarCount: { background: '#e63946', color: '#fff', borderRadius: '20px', padding: '2px 8px', fontSize: '11px', fontWeight: 'bold' },
  logoutBtn: { marginTop: '12px', padding: '12px 16px', background: '#fff5f5', color: '#e63946', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', textAlign: 'left', width: '100%' },
  main: { minHeight: '400px' },
  loadingBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' },
  loadingText: { color: '#aaa', fontSize: '16px' },
  sectionTitle: { color: '#1a1a2e', fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
  emptyBox: { background: '#fff', borderRadius: '16px', padding: '60px 40px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  emptyIcon: { fontSize: '48px', display: 'block', marginBottom: '16px' },
  emptyTitle: { color: '#1a1a2e', fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' },
  emptyText: { color: '#888', fontSize: '14px', marginBottom: '20px' },
  actionBtn: { padding: '12px 28px', background: '#e63946', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  card: { background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  cardId: { color: '#1a1a2e', fontWeight: 'bold', fontSize: '16px', margin: 0 },
  cardDate: { color: '#aaa', fontSize: '13px', margin: '4px 0 0' },
  statusBadge: { padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' },
  timeline: { display: 'flex', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #f0f2f5' },
  timelineStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', flex: 1 },
  timelineDot: { width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 },
  timelineTick: { color: '#fff', fontSize: '12px', fontWeight: 'bold' },
  timelineLine: { position: 'absolute', top: '14px', left: '50%', width: '100%', height: '2px', zIndex: 0 },
  timelineLabel: { fontSize: '11px', marginTop: '8px', textAlign: 'center' },
  cardItems: { marginBottom: '16px' },
  cardItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f8f9fa' },
  itemDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#e63946', flexShrink: 0 },
  itemText: { flex: 1, color: '#555', fontSize: '14px' },
  itemPrice: { color: '#1a1a2e', fontSize: '13px', fontWeight: '600' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' },
  cardAddress: { color: '#888', fontSize: '13px' },
  cardTotal: { color: '#555', fontSize: '14px' },
  pendingNote: { background: '#fffbeb', color: '#856404', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginTop: '12px', border: '1px solid #fde68a' },
  contactedNote: { background: '#eff6ff', color: '#1d4ed8', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginTop: '12px', border: '1px solid #bfdbfe' },
  confirmedNote: { background: '#f0fdf4', color: '#15803d', padding: '12px 16px', borderRadius: '8px', fontSize: '13px', marginTop: '12px', border: '1px solid #bbf7d0' },
  serviceInfo: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' },
  serviceInfoItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  serviceInfoLabel: { color: '#888', fontSize: '12px', fontWeight: '600' },
  serviceInfoValue: { color: '#1a1a2e', fontSize: '14px', fontWeight: '500' },
  messageBox: { background: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '12px' },
  messageLabel: { color: '#888', fontSize: '12px', fontWeight: '600', marginBottom: '8px' },
  messageText: { color: '#333', fontSize: '14px', lineHeight: 1.7, margin: 0 },
  replyBox: { background: '#f0fdf4', borderRadius: '10px', padding: '16px', border: '1px solid #bbf7d0' },
  replyLabel: { color: '#15803d', fontSize: '12px', fontWeight: '600', marginBottom: '8px' },
  replyText: { color: '#333', fontSize: '14px', lineHeight: 1.7, margin: 0 },
  profileCard: { background: '#fff', borderRadius: '16px', padding: '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e8eaf0' },
  profileGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { marginBottom: '4px' },
  label: { display: 'block', color: '#555', fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  input: { width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e8eaf0', background: '#f8f9fa', color: '#333', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
  divider: { height: '1px', background: '#f0f2f5', margin: '24px 0' },
  passwordTitle: { color: '#1a1a2e', fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' },
  optional: { color: '#aaa', fontSize: '13px', fontWeight: 'normal' },
  saveBtn: { marginTop: '20px', padding: '12px 32px', background: '#e63946', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', boxShadow: '0 4px 16px rgba(230,57,70,0.3)' },
  successBox: { background: '#f0fdf4', color: '#15803d', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', border: '1px solid #bbf7d0' },
  errorBox: { background: '#fff5f5', color: '#e63946', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', border: '1px solid #ffd0d3' }
};

export default MyAccountPage;